import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useQueries } from '@tanstack/react-query';
import { Archive, FileSearch, GitBranch, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import {
  SupersedeChainViz,
  type SupersedeChainNode,
} from '@/components/contract/supersede-chain-viz';
import { RegisterChangeOrderModal } from '@/components/modals/register-change-order-modal';
import { RemoveChangeOrderVersionModal } from '@/components/modals/remove-change-order-version-modal';
import { ChangeCard } from '@/components/contract/change-card';
import { ContractHeader } from '@/components/contract/contract-header';
import { useEdlListQuery } from '@/lib/api/deliverables';
import {
  getChangeOrder,
  useChangeOrderChainQuery,
  useChangeOrderQuery,
  useRemoveChangeOrderVersionMutation,
  type ChangeOrder,
  type DetectedChangeOrder,
} from '@/lib/api/change-orders';
import { describeApiError } from '@/lib/api/errors';
import { AiSurface } from '@/components/ui/ai-surface';
import { formatDate } from '@/lib/utils/format';
import { changeOrderCode } from '@/lib/contract-labels';

/**
 * /contract/changes — C20-133 prototype-parity rewrite (T-0102 / E06-F02).
 *
 * Visual contract: `screens-modules-tabs.js → contractChanges()` (lines
 * 426-468). Layout:
 *
 *   <ContractHeader actions="CO history · New change order" />
 *   <KPI strip>                Active CO count · Draft/under review · Cumulative delta
 *   <SupersedeChainViz>        SVG timeline; click node to inspect
 *   <ChangeCard>               Selected version's revisions diff
 *
 * Wire-up matrix:
 *   - Chain walk seed          → ?sow=<uuid> URL search param
 *                                (set by baseline-screen SOW pinner +
 *                                "SOW committed" toast in T-0103 / T-0104)
 *   - Per-version metadata     → useChangeOrderQuery (T-0064)
 *   - Chain pointers           → ChangeOrder.superseded_sow_id /
 *                                superseded_by_sow_id walked client-side
 *                                with a defensive 8-hop bound
 *   - "New change order"       → data-create="change-order" placeholder for
 *                                T-0114 modal hook-up
 *   - "CO history"             → archive deep-link (future)
 *
 * Backend gaps surfaced as Pending (never faked):
 *   - There's no `/api/v1/engagements/:id/sow` list endpoint yet — when no
 *     `?sow=` seed is in the URL the screen renders a helpful empty-state
 *     pointing the user back to Baseline's SOW pinner. Flagged in
 *     `change-orders.ts`.
 *   - Cumulative `$` delta is not on the backend — the prototype shows
 *     "+$0.40 M". We render "Pending" for the dollar tile and surface the
 *     revisions-count totals instead (the deterministic counterpart).
 */
const changesSearchSchema = z.object({
  sow: z.string().uuid().optional(),
});

export const Route = createFileRoute('/engagements/$engagementId/contract/changes')({
  component: ContractChangesTab,
  validateSearch: changesSearchSchema,
});

const MAX_CHAIN_HOPS = 8;

function ContractChangesTab() {
  const { engagementId } = useParams({ from: '/engagements/$engagementId/contract/changes' });
  const search = Route.useSearch();

  // The engagement's whole SOW chain + change-order documents detected but
  // not yet registered. Seeds the chain walk when the URL carries no
  // `?sow=` (which is the normal way to arrive: via the tab strip).
  const chain = useChangeOrderChainQuery(engagementId);
  // The chain endpoint lists every SOW version for the engagement, so it is
  // the authoritative set. The pointer walk below remains as a fallback for
  // a `?sow=` deep link while the list is still loading.
  const listedSowIds = useMemo(() => {
    const c = chain.data;
    if (!c) return [] as string[];
    return [...(c.baseline ? [c.baseline.sow_id] : []), ...c.change_orders.map((co) => co.sow_id)];
  }, [chain.data]);
  const headSowId = chain.data?.change_orders.at(-1)?.sow_id ?? chain.data?.baseline?.sow_id;
  // A `?sow=` the chain no longer lists (a version removed since the link
  // was made, C2-51) seeds from the head instead of a 404.
  const seedSowId =
    search.sow && (!chain.data || listedSowIds.includes(search.sow)) ? search.sow : headSowId;
  const detected = chain.data?.detected ?? [];

  // For the header meta line — total deliverables tracked.
  const edl = useEdlListQuery(engagementId);

  // Seed the chain walk from the URL-provided SOW id.
  const seedQuery = useChangeOrderQuery(seedSowId);

  // Discover every other SOW in the chain by following the
  // superseded_sow_id / superseded_by_sow_id pointers.
  const [chainSowIds, setChainSowIds] = useState<string[]>([]);
  const [registering, setRegistering] = useState(false);
  // Undo a registration (C2-51): the confirm dialog for the chain head.
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const removeVersion = useRemoveChangeOrderVersionMutation(engagementId);
  // Document preselected in the register modal ("Register" on a detected row).
  const [registerDocumentId, setRegisterDocumentId] = useState<string | undefined>(undefined);
  const [walkError, setWalkError] = useState<unknown | null>(null);

  useEffect(() => {
    if (!seedQuery.data) {
      setChainSowIds([]);
      return;
    }
    let cancelled = false;
    setWalkError(null);

    (async () => {
      const seed = seedQuery.data!;
      const visited = new Set<string>([seed.sow_id]);
      const collected: ChangeOrder[] = [seed];

      // Walk backwards through superseded_sow_id pointers.
      let cursor = seed.superseded_sow_id;
      let hops = 0;
      while (cursor && hops < MAX_CHAIN_HOPS && !visited.has(cursor)) {
        try {
          const next = await getChangeOrder(cursor);
          if (cancelled) return;
          visited.add(next.sow_id);
          collected.unshift(next);
          cursor = next.superseded_sow_id;
          hops += 1;
        } catch (e) {
          if (cancelled) return;
          setWalkError(e);
          return;
        }
      }

      // Walk forwards through superseded_by_sow_id pointers.
      cursor = seed.superseded_by_sow_id;
      hops = 0;
      while (cursor && hops < MAX_CHAIN_HOPS && !visited.has(cursor)) {
        try {
          const next = await getChangeOrder(cursor);
          if (cancelled) return;
          visited.add(next.sow_id);
          collected.push(next);
          cursor = next.superseded_by_sow_id;
          hops += 1;
        } catch (e) {
          if (cancelled) return;
          setWalkError(e);
          return;
        }
      }

      if (!cancelled) {
        setChainSowIds(collected.map((co) => co.sow_id));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [seedQuery.data]);

  const effectiveSowIds = listedSowIds.length > 0 ? listedSowIds : chainSowIds;

  // Fetch every chain SOW with react-query so the selected card re-renders
  // deterministically off cache rather than off the imperative walk.
  const chainQueries = useQueries({
    queries: effectiveSowIds.map((sowId) => ({
      queryKey: ['change-order', sowId] as const,
      queryFn: () => getChangeOrder(sowId),
      enabled: Boolean(sowId),
    })),
  });
  const chainCos: ReadonlyArray<ChangeOrder> = useMemo(
    () => chainQueries.flatMap((q) => (q.data ? [q.data] : [])),
    [chainQueries],
  );

  // Sort by version_seq ascending (oldest first) — the SVG renders left-to-right.
  const sortedChain = useMemo(() => {
    const out = [...chainCos];
    out.sort((a, b) => a.version_seq - b.version_seq);
    return out;
  }, [chainCos]);

  // The head of the chain: the 'current' SOW, else the highest version.
  const currentCo = useMemo(
    () => sortedChain.find((co) => co.status === 'current') ?? sortedChain.at(-1) ?? null,
    [sortedChain],
  );

  // Selection follows the current version until the user clicks a node.
  // (The chain loads one version at a time and grows when a change order
  // is registered; a selection made from a partial chain must not stick.)
  const [pickedSowId, setPickedSowId] = useState<string | null>(null);
  const selectedSowId =
    pickedSowId && sortedChain.some((co) => co.sow_id === pickedSowId)
      ? pickedSowId
      : (currentCo?.sow_id ?? null);
  const setSelectedSowId = setPickedSowId;

  const selectedCo = useMemo(
    () => sortedChain.find((co) => co.sow_id === selectedSowId) ?? null,
    [sortedChain, selectedSowId],
  );

  const nodes: ReadonlyArray<SupersedeChainNode> = useMemo(
    () =>
      sortedChain.map((co) => ({
        sow_id: co.sow_id,
        versionLabel: `v${co.version_seq}`,
        effectiveDate: co.effective_date,
        status: co.status,
        // The version_seq doubles as the CO identifier (v1 → CO-001), the
        // same code a deliverable's "From CO-001" source uses (C2-22).
        changeOrderLabel: co.version_seq > 0 ? changeOrderCode(co.version_seq) : null,
      })),
    [sortedChain],
  );

  // Aggregate counts for the KPI strip across all revisions in the chain.
  const totals = useMemo(() => {
    const acc = { added: 0, modified: 0, removed: 0 };
    for (const co of sortedChain) {
      for (const rev of co.revisions) {
        if (rev.change_type === 'added') acc.added += 1;
        else if (rev.change_type === 'modified') acc.modified += 1;
        else if (rev.change_type === 'removed') acc.removed += 1;
      }
    }
    return acc;
  }, [sortedChain]);

  // Status counts — distinguishes the "active" (current) version from
  // anything in flight (draft / proposed). The backend `status` vocabulary
  // is permissive on the wire; we treat `current` as active and everything
  // else as a non-current revision in flight.
  const statusCounts = useMemo(() => {
    let active = 0;
    let inFlight = 0;
    for (const co of sortedChain) {
      if (co.status === 'current') active += 1;
      else if (co.status === 'proposed' || co.status === 'draft' || co.status === 'review')
        inFlight += 1;
    }
    return { active, inFlight };
  }, [sortedChain]);

  const isLoading =
    chain.isLoading ||
    (Boolean(seedSowId) && (seedQuery.isLoading || chainQueries.some((q) => q.isLoading)));

  const error =
    chain.error ?? seedQuery.error ?? walkError ?? chainQueries.find((q) => q.error)?.error;

  const hasAnyChain = sortedChain.length > 0;

  // Only the chain head above the baseline can be removed; the server
  // refuses anything else, and a version with applied revisions, with a 409.
  const removable =
    selectedCo && selectedCo.status === 'current' && selectedCo.version_seq > 0 ? selectedCo : null;
  const removableHead = removable
    ? (chain.data?.change_orders.find((co) => co.sow_id === removable.sow_id) ?? null)
    : null;
  const priorVersionSeq = removable
    ? (sortedChain.find((co) => co.sow_id === removable.superseded_sow_id)?.version_seq ??
      removable.version_seq - 1)
    : 0;
  const confirmRemove = () => {
    if (!removable) return;
    setRemoveError(null);
    removeVersion.mutate(
      { sowId: removable.sow_id },
      {
        onSuccess: () => {
          setRemoving(false);
          // Selection follows the restored head.
          setPickedSowId(null);
        },
        onError: (e) =>
          setRemoveError(describeApiError(e, {}, 'The version could not be removed.')),
      },
    );
  };

  return (
    <div className="space-y-6">
      <ContractHeader
        engagementId={engagementId}
        active="changes"
        meta={
          <span>
            {sortedChain.length} {sortedChain.length === 1 ? 'SOW version' : 'SOW versions'} in
            chain · {edl.data?.entries.length ?? '—'} deliverables tracked
          </span>
        }
        actions={
          <>
            <Button
              type="button"
              size="sm"
              className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
              onClick={() => {
                setRegisterDocumentId(undefined);
                setRegistering(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              New change order
            </Button>
          </>
        }
      />

      {detected.length > 0 ? (
        <DetectedChangeOrders
          engagementId={engagementId}
          detected={detected}
          onRegister={(documentId) => {
            setRegisterDocumentId(documentId);
            setRegistering(true);
          }}
        />
      ) : null}

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center gap-2 p-6 text-body-sm text-ink-500">
            <Spinner /> Loading the SOW chain…
          </CardContent>
        </Card>
      ) : error ? (
        <ErrorMessage error={error} />
      ) : !seedSowId ? (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title="No change orders yet"
              description={
                chain.data?.baseline_candidate
                  ? `The baseline will be anchored to "${chain.data.baseline_candidate.document_title}" when the first change order is registered. Upload a change order document and it will appear here for registration, or use New change order.`
                  : 'Upload the original SOW first. Once it is classified as a SOW, change orders can be registered against it. Uploaded change order documents appear here automatically.'
              }
            />
          </CardContent>
        </Card>
      ) : !hasAnyChain ? (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title="No SOW versions yet"
              description="Register a baseline SOW from the Documents tab — once a SOW is committed it'll appear here. Subsequent change orders fan out into the supersede chain visualization."
            />
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <KpiTile
              label="Active SOW"
              value={statusCounts.active}
              caption={currentCo ? `effective ${formatDate(currentCo.effective_date)}` : '—'}
              tone="healthy"
            />
            <KpiTile
              label="Draft / under review"
              value={statusCounts.inFlight}
              caption={`${totals.added} added · ${totals.modified} modified · ${totals.removed} removed`}
              tone={statusCounts.inFlight > 0 ? 'at-risk' : 'neutral'}
            />
            <KpiTile
              label="Revisions applied"
              value={totals.added + totals.modified + totals.removed}
              caption={`across ${sortedChain.filter((co) => co.version_seq > 0).length} change order(s)`}
              tone="neutral"
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-h3 font-semibold text-ink-900">
                <GitBranch className="h-4 w-4" aria-hidden="true" />
                Supersede chain
              </CardTitle>
              <p className="text-meta text-ink-500">
                Newer orders supersede older clauses; superseded values stay queryable in audit +
                archive. Click a node to inspect that version's revisions.
              </p>
            </CardHeader>
            <CardContent>
              <SupersedeChainViz
                nodes={nodes}
                selectedSowId={selectedSowId}
                onSelect={setSelectedSowId}
              />
            </CardContent>
          </Card>

          {selectedCo ? (
            <ChangeCard
              sow_id={selectedCo.sow_id}
              versionLabel={`v${selectedCo.version_seq}`}
              effectiveDate={selectedCo.effective_date}
              status={selectedCo.status}
              revisions={selectedCo.revisions}
              engagementId={engagementId}
              onRemove={
                removable
                  ? () => {
                      setRemoveError(null);
                      setRemoving(true);
                    }
                  : undefined
              }
            />
          ) : null}
        </>
      )}

      <RegisterChangeOrderModal
        engagementId={engagementId}
        open={registering}
        onOpenChange={setRegistering}
        initialDocumentId={registerDocumentId}
      />
      {removable ? (
        <RemoveChangeOrderVersionModal
          open={removing}
          onOpenChange={(next) => {
            setRemoving(next);
            if (!next) setRemoveError(null);
          }}
          versionLabel={`v${removable.version_seq}`}
          priorVersionLabel={`v${priorVersionSeq}`}
          documentTitle={removableHead?.document_title ?? null}
          revisionCount={removable.revisions.length}
          removing={removeVersion.isPending}
          error={removeError}
          onConfirm={confirmRemove}
        />
      ) : null}
    </div>
  );
}

/**
 * Change-order documents the classifier found among the uploads that have
 * not been registered into the SOW chain yet. Detection is automatic;
 * registering (which supersedes the current SOW) stays a human decision.
 * AI surface treatment because the label comes from the classifier.
 */
function DetectedChangeOrders({
  engagementId,
  detected,
  onRegister,
}: {
  engagementId: string;
  detected: ReadonlyArray<DetectedChangeOrder>;
  onRegister: (documentId: string) => void;
}) {
  return (
    <AiSurface className="rounded-card p-4" aria-labelledby="detected-co-title">
      <h2
        id="detected-co-title"
        className="flex items-center gap-2 text-h3 font-semibold text-ai-ink"
      >
        <FileSearch className="h-4 w-4" aria-hidden="true" />
        {detected.length === 1
          ? '1 uploaded change order is not registered yet'
          : `${detected.length} uploaded change orders are not registered yet`}
      </h2>
      <p className="mt-1 text-meta">
        Detected from uploaded documents. Where the AI has read one, review its proposed revisions
        and approve to register the change order and apply them in one step. You can also register a
        document by hand.
      </p>
      <ul className="mt-3 space-y-2">
        {detected.map((d) => (
          <li
            key={d.document_id}
            className="flex flex-wrap items-start justify-between gap-3 rounded-card border border-ai-surface-border bg-paper-0 px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate text-body-sm font-medium text-ink-900">
                {d.document_title}
              </div>
              <div className="text-meta text-ink-500">
                Uploaded {formatDate(d.uploaded_at)} · classifier confidence{' '}
                <span className="font-mono">{Math.round(d.classification_confidence * 100)}%</span>
              </div>
              {d.analysis ? (
                <div className="mt-1.5 space-y-0.5 text-body-sm text-ink-700">
                  {d.analysis.summary ? <p>{d.analysis.summary}</p> : null}
                  <p className="text-meta text-ink-500">
                    AI read: <span className="font-mono">{d.analysis.revision_counts.added}</span>{' '}
                    added · <span className="font-mono">{d.analysis.revision_counts.modified}</span>{' '}
                    modified ·{' '}
                    <span className="font-mono">{d.analysis.revision_counts.removed}</span> removed
                    {d.analysis.effective_date
                      ? ` · effective ${formatDate(d.analysis.effective_date)}`
                      : ' · no effective date found'}
                  </p>
                </div>
              ) : d.ready_to_register ? (
                <p className="mt-1.5 text-meta text-ink-500">
                  No AI analysis yet. It appears here when the read finishes.
                </p>
              ) : null}
            </div>
            <div className="flex flex-none items-center gap-2">
              {d.analysis ? (
                <Button asChild type="button" size="sm">
                  <Link to="/engagements/$engagementId/proposals" params={{ engagementId }}>
                    Review revisions
                  </Link>
                </Button>
              ) : null}
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!d.ready_to_register}
                title={d.ready_to_register ? undefined : 'Still being read. Available in a moment.'}
                onClick={() => onRegister(d.document_id)}
              >
                {d.ready_to_register ? 'Register by hand' : 'Processing…'}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </AiSurface>
  );
}

function KpiTile({
  label,
  value,
  caption,
  tone = 'neutral',
}: {
  label: string;
  value: number | string;
  caption?: string;
  tone?: 'neutral' | 'healthy' | 'at-risk' | 'critical';
}) {
  const accent =
    tone === 'critical'
      ? 'border-l-posture-critical'
      : tone === 'at-risk'
        ? 'border-l-accent-amber-600'
        : tone === 'healthy'
          ? 'border-l-posture-healthy'
          : 'border-l-ink-200';
  const valueClass =
    tone === 'critical'
      ? 'text-posture-critical'
      : tone === 'at-risk'
        ? 'text-accent-amber-700'
        : 'text-ink-900';
  return (
    <Card className={`overflow-hidden border-l-[3px] ${accent}`}>
      <CardContent className="p-4">
        <div className="text-meta uppercase tracking-wide text-ink-500">{label}</div>
        <div className={`mt-1 text-h2 font-semibold tabular-nums ${valueClass}`}>{value}</div>
        {caption ? <div className="mt-1 text-meta text-ink-500">{caption}</div> : null}
      </CardContent>
    </Card>
  );
}

export { ContractChangesTab };
